import React, { useContext, useState } from "react";
import styled from "styled-components";
import { useFormikContext } from "formik";
import * as Yup from "yup";
import analytics from "analytics";
import { CheckoutContext } from "contexts/CheckoutProvider";
import StepLayout, { StepLayoutProps } from "components/Step/StepLayout";
import {
  ClubRecommendationResponseCamelCase,
  FormikSubmitStep,
  ReaderProfileKey as Key,
  ReaderProfileSubmit,
  ReaderProfileValues,
  ReaderProfileValuesPartial,
} from "components/Step/stepTypes";
import {
  getContentfulQuizStep,
  gsuSubmitAnswersDefault,
} from "components/Step/stepUtil";
import GuidedSignUpLayoutReaderProfile from "components/GuidedSignUp/GuidedSignUpLayoutReaderProfile";
import LegacyThemeProvider from "components/Layout/DefaultThemeProvider";
import DupSubWarningModal from "components/GuidedSignUp/DupSubWarningModal";

export const VALIDATION_SCHEMA = Yup.object().shape({
  [Key.MONTH]: Yup.string().required("Required."),
  [Key.YEAR]: Yup.string().required("Required."),
  [Key.NAME]: Yup.string().required("Required."),
});

export const INITIAL_VALUES: ReaderProfileValues = {
  [Key.KIND]: "reader-profile",
  [Key.FIXED_AGE]: "",
  [Key.MONTH]: "",
  [Key.NAME]: "",
  [Key.YEAR]: "",
  [Key.SKIP_DUP_MODAL]: false,
} as const;

export function getReaderProfileStep(contentfulQuizModel?: any) {
  const step = getContentfulQuizStep(
    contentfulQuizModel,
    "reader-profile-funnel"
  );

  return {
    ...step,
    prompt: "Let’s start with the basics.",
    sectionHeaders: [
      "Please confirm your reader’s name.",
      "What month and year were they born?",
    ],
  };
}

const StyledGuidedSignUpLayoutReaderProfile = styled(
  GuidedSignUpLayoutReaderProfile
)`
  padding: 2rem 0;
`;

function ContentWithFormik({
  modalLoading = false,
  modalOpen = false,
  possibleDuplicateSubId,
  onClose,
  onDupSubModalConfirm,
  step,
}: Omit<StepReaderProfileProps, "onSubmit" | "contentfulQuizModel"> & {
  modalLoading?: boolean;
  modalOpen?: boolean;
  onClose: () => void;
  onDupSubModalConfirm: FormikSubmitStep<ReaderProfileValues>;
  possibleDuplicateSubId?: number;
  step: any;
}) {
  const { values, ...formikHelpers } = useFormikContext<ReaderProfileValues>();

  const handleDupSubModalConfirm = () => {
    onDupSubModalConfirm(
      { ...values, [Key.SKIP_DUP_MODAL]: true },
      formikHelpers
    );
  };

  const childName = values?.[Key.NAME];
  const subId = possibleDuplicateSubId ? String(possibleDuplicateSubId) : null;

  return (
    <LegacyThemeProvider>
      <StyledGuidedSignUpLayoutReaderProfile
        childName={childName}
        step={step}
      />
      {subId && (
        <DupSubWarningModal
          isOpen={modalOpen}
          onClose={onClose}
          childName={childName}
          onDupSubModalConfirm={handleDupSubModalConfirm}
          subId={subId}
          loading={modalLoading}
        />
      )}
    </LegacyThemeProvider>
  );
}

export type StepLayoutPropsReaderProfile = Omit<
  StepLayoutProps<ReaderProfileValues>,
  "asForm" | "children" | "formId"
>;

export interface StepReaderProfileProps extends StepLayoutPropsReaderProfile {
  contentfulQuizModel: any;
  onSubmit: FormikSubmitStep<ReaderProfileValues>;
  readerProfileValues?: ReaderProfileValuesPartial;
}

export const StepReaderProfile = ({
  contentfulQuizModel,
  onSubmit,
  readerProfileValues,
  ...stepLayoutProps
}: StepReaderProfileProps) => {
  // prevents re-render after submit, otherwise validates after submit if set directly on formik as spread props
  const [initialValues] = useState({
    ...INITIAL_VALUES,
    ...readerProfileValues,
  });

  const { updateChildInfo, updateSelectedClub } = useContext(CheckoutContext);

  const [step] = useState(getReaderProfileStep(contentfulQuizModel));

  const [possibleDuplicateSubId, setPossibleDuplicateSubId] = useState<
    number | undefined
  >();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const handleSubmit: ReaderProfileSubmit = async (values, formikHelpers) => {
    const childInfo = (await updateChildInfo?.({
      birthMonth: Number(values[Key.MONTH]),
      birthYear: Number(values[Key.YEAR]),
      firstName: values[Key.NAME],
    })) as ClubRecommendationResponseCamelCase;

    const {
      possibleDuplicateSubId: dupId,
      primarySuggestedClub,
      secondarySuggestedClubs,
      age: calculatedAge,
    } = childInfo || {};

    values[Key.CALCULATED_AGE] = calculatedAge;

    analytics?.trackAmplitudeClickEvent("child age", calculatedAge);
    analytics?.setAmplitudeUserProperties({ calculated_age: calculatedAge });

    const suggestedClub = primarySuggestedClub || secondarySuggestedClubs?.[0];
    await updateSelectedClub?.(suggestedClub);

    if (!values[Key.SKIP_DUP_MODAL] && dupId) {
      setPossibleDuplicateSubId(dupId);
      setModalOpen(true);
    } else {
      setModalLoading(false);
      setModalOpen(false);
      return await gsuSubmitAnswersDefault({
        contentfulQuizModel,
        formikHelpers,
        onError: (res) => res,
        onSubmit,
        step,
        values: { ...values, [Key.SKIP_DUP_MODAL]: false },
      });
    }
  };

  const handleDupSubModalConfirm: ReaderProfileSubmit = async (
    values,
    formikHelpers
  ) => {
    setModalLoading(true);
    return await handleSubmit(
      { ...values, [Key.SKIP_DUP_MODAL]: true },
      formikHelpers
    );
  };

  const handleClose = () => {
    setModalOpen(false);
  };

  return (
    <StepLayout<ReaderProfileValues>
      asForm
      bgColor="white"
      formId="step-reader-profile"
      formikProps={{
        initialValues,
        onSubmit: handleSubmit,
        validationSchema: VALIDATION_SCHEMA,
      }}
      {...stepLayoutProps}
    >
      <ContentWithFormik
        modalLoading={modalLoading}
        modalOpen={modalOpen}
        onClose={handleClose}
        onDupSubModalConfirm={handleDupSubModalConfirm}
        possibleDuplicateSubId={possibleDuplicateSubId}
        step={step}
      />
    </StepLayout>
  );
};

export default StepReaderProfile;
